import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import downloadFile from "_shared/_utilities/download-file";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { Masked } from "work/_shared/masked-fields";

const ListContainer = styled(Grid)(() => ({
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "20rem",
  width: "100%",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  overflowY: "auto",
}));
const HeadControls = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));
const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type PoliciesTabProps = {
  proposal: Proposal;
  commentThreads?: CommentThread[];
  onCommentsClicked: (field: ProposalField, name?: string) => void;
};

export default function PoliciesTab(props: Readonly<PoliciesTabProps>) {
  const { proposal, commentThreads, onCommentsClicked } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const policyDocument = await documentApiService.downloadDocument(documentId);
      await downloadFile(policyDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download the document. Please try again later.", {
        variant: "error",
      });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  const selectedDocuments = session.context?.viewingAsVendor ? proposal.vendorPolicies : proposal.clientPolicies;

  if (Masked.isMasked(selectedDocuments)) {
    return "You do not have permission to view policy documents for this archived proposal.";
  }
  return (
    <>
      <HeadControls>
        {selectedDocuments.length === 0 && <HelperText>No policy documents</HelperText>}
        <CommentsButton
          commentThreads={commentThreads}
          hideUnreadCount={true}
          field={ProposalField.Discount}
          toolTip={
            commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.Discount))
              ? "Comments"
              : "No Comments"
          }
          onCommentsClicked={
            onCommentsClicked
              ? () => {
                  onCommentsClicked(ProposalField.Discount);
                }
              : undefined
          }
        />
      </HeadControls>
      <ListContainer container direction="row">
        <Grid>
          {selectedDocuments.length > 0 && (
            <DocumentList
              documents={selectedDocuments}
              documentType={
                session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
              }
              downloadingFileId={isDownloadingFile}
              commentThreads={commentThreads}
              onCommentsClicked={onCommentsClicked}
              onDownload={handleDownloadDocumentById}
            />
          )}
        </Grid>
      </ListContainer>
    </>
  );
}
